// 260923 Epic2-P1/P2 Bước 8/7 — logic mode prompt (Kỹ thuật/Tự nhiên) THUẦN, không import antd (chạy được
// dưới jest). Mirror 1:1 admin-service/src/utils/filmPromptMode.ts — nguồn sự thật là BE, file này chỉ để UI
// tính nhãn/khoá nút NGAY (không đợi round-trip) bằng ĐÚNG câu chữ BE sẽ trả. BE luôn là cửa thật (409 nếu FE tính sai).
import type { FilmPromptMode, PromptModeState, AuthorWorkState } from '../../types/film';
import { isBeatMetaV2 } from './beatSequenceUtils';

const PROMPT_MODES: FilmPromptMode[] = ['TECHNICAL', 'NATURAL'];
export const MODE_LABEL_VI: Record<FilmPromptMode, string> = { TECHNICAL: 'Kỹ thuật', NATURAL: 'Tự nhiên' };

export function normalizePromptMode(raw: unknown): FilmPromptMode | null {
  if (typeof raw !== 'string') return null;
  const v = raw.trim().toUpperCase();
  return (PROMPT_MODES as string[]).includes(v) ? (v as FilmPromptMode) : null;
}

export function resolvePromptMode(eventMode: unknown, planMode: unknown): FilmPromptMode {
  const e = normalizePromptMode(eventMode);
  if (e) return e;
  return normalizePromptMode(planMode) ?? 'TECHNICAL';
}

export function snapshotModeOf(authorPrompt: string | null | undefined, authorPromptMode: unknown): FilmPromptMode | null {
  if (!authorPrompt || !authorPrompt.trim()) return null;
  return normalizePromptMode(authorPromptMode) ?? 'TECHNICAL';
}

export function promptModeStateOf(args: { effectiveMode: FilmPromptMode; authorPrompt: string | null | undefined; authorPromptMode: unknown }): PromptModeState {
  const snap = snapshotModeOf(args.authorPrompt, args.authorPromptMode);
  if (snap === null) return 'MISSING';
  return snap === args.effectiveMode ? 'OK' : 'MISMATCH';
}

/**
 * 260923 Epic2-P2 (N10) — thêm tham số 3 `canAuthor` (mặc định `false` ⇒ quên truyền thì ra `blocked`,
 * không bao giờ lặp vô hạn). `MISSING` + `canAuthor` ⇒ `todo` (CẢ HAI mode); `MISSING` + không ⇒ `blocked`.
 */
export function authorWorkStateOf(state: PromptModeState, effectiveMode: FilmPromptMode, canAuthor: boolean = false): AuthorWorkState {
  if (state === 'OK') return 'done';
  if (state === 'MISMATCH') return 'blocked';
  return canAuthor ? 'todo' : 'blocked';
}

/**
 * 260923 Epic2-P2 (N10) — mirror BE `canAuthor`. `TECHNICAL` ⇒ cần shot. `NATURAL` ⇒ cần event v2 (có
 * chuỗi nhịp) VÀ (≥1 nhịp còn chữ sau `trim` HOẶC `spokenLineCount > 0` — D1) VÀ có shot (refs vẫn lắp từ
 * shot, N12). 260925 (D1, sửa sau blackbox bước 1–2) — event CHỈ CÓ THOẠI (0 nhịp) là event Epic 1 hợp lệ,
 * không được `blocked` vĩnh viễn chỉ vì không có nhịp hành động.
 */
export function canAuthorOf(args: { mode: FilmPromptMode; beatMeta: unknown; shotCount: number; spokenLineCount: number }): boolean {
  if (args.mode === 'TECHNICAL') return args.shotCount > 0;
  if (!isBeatMetaV2(args.beatMeta)) return false;
  // 260925 (R1, sửa sau review code + probe, mirror BE) — `actionBeats` có thể là dữ liệu HỎNG (chuỗi,
  // object, phần tử không phải object, `text` sai kiểu) chứ không chỉ mảng đúng shape. Không mảng ⇒ [], không throw.
  const rawBeats = (args.beatMeta as { actionBeats?: unknown } | null)?.actionBeats;
  const beats = Array.isArray(rawBeats) ? rawBeats : [];
  const hasBeatText = beats.some((b) => typeof (b as { text?: unknown })?.text === 'string' && !!(b as { text: string }).text.trim());
  return (hasBeatText || args.spokenLineCount > 0) && args.shotCount > 0;
}

export interface GenGateInput {
  effectiveMode: FilmPromptMode;
  authorPrompt: string | null | undefined;
  authorPromptMode: unknown;
  promptOverride?: string | null;
}
export type GenGateResult = { ok: true } | { ok: false; message: string };

/** Mirror BE `genPromptGate` — CÙNG câu chữ, dùng để khoá nút + tooltip trước khi gọi API. */
export function genGate(args: GenGateInput): GenGateResult {
  if (args.promptOverride && args.promptOverride.trim()) return { ok: true };
  const state = promptModeStateOf({ effectiveMode: args.effectiveMode, authorPrompt: args.authorPrompt, authorPromptMode: args.authorPromptMode });
  if (state === 'OK') return { ok: true };
  if (state === 'MISMATCH') {
    const snap = snapshotModeOf(args.authorPrompt, args.authorPromptMode) as FilmPromptMode;
    return {
      ok: false,
      message: `Prompt đang lưu là mode ${MODE_LABEL_VI[snap]}, event đang ở mode ${MODE_LABEL_VI[args.effectiveMode]}`
        + ` — đổi event về ${MODE_LABEL_VI[snap]}, hoặc dựng lại prompt.`,
    };
  }
  // 260923 Epic2-P2 (N11) — CÙNG CHỮ với BE `genPromptGate` (filmPromptMode.ts).
  if (args.effectiveMode === 'NATURAL') return { ok: false, message: 'Event chưa có prompt — bấm dựng prompt trước (event đời cũ không có chuỗi nhịp: viết tay prompt hoặc đổi event về Kỹ thuật).' };
  return { ok: false, message: 'Event chưa có prompt — bấm dựng prompt trước.' };
}

export interface EventModeLabelInput {
  promptMode?: FilmPromptMode | null;
  authorPromptMode?: FilmPromptMode | null;
  authorStatus?: string | null;
  effectiveMode: FilmPromptMode;
  state: PromptModeState;
}
export interface EventModeLabel {
  /** "Tự nhiên" | "Kỹ thuật" */
  modeText: string;
  /** '' (theo plan) | ' · riêng' (đè tay) | ' · giữ Kỹ thuật (prompt sửa tay)' (ghim tự động — N5) */
  suffix: string;
  /** Tag trạng thái cạnh nhãn mode (khu ADVANCED) — null khi OK (không cần tag phụ). */
  stateTag: { color: 'red' | 'orange'; text: string } | null;
  /**
   * 260923 Epic2-P1 F2 — tag NGẮN cho hàng đầu thẻ (`sb-head`, ngoài ADVANCED): null khi OK (không cần nói
   * gì thêm ngay đầu thẻ). Chữ ngắn hơn `stateTag` (không lặp lại "prompt"/"chưa gen được" — chỗ đó chật).
   */
  head: { text: string; cls: 'block' | 'warn' } | null;
  /** Có đè/ghim (promptMode khác null) hay không — quyết định có hiện thêm tag mode ở hàng đầu thẻ. */
  showModeInHead: boolean;
}

/**
 * Nhãn thẻ event: mode hiệu lực + hậu tố (đè tay vs GHIM tự động do đổi mode cả plan — N5) + tag trạng thái
 * (MISMATCH đỏ / MISSING cam). Ghim (L7) và đè tay (ADVANCED) đều để lại `promptMode` khác null trên event;
 * phân biệt bằng: ghim luôn đi kèm `authorStatus==='EDITED'` VÀ đúng bằng snapshot của chính nó.
 */
export function eventModeLabel(input: EventModeLabelInput): EventModeLabel {
  const modeText = MODE_LABEL_VI[input.effectiveMode];
  let suffix = '';
  const showModeInHead = !!input.promptMode;
  if (input.promptMode) {
    const isPin = input.authorStatus === 'EDITED' && !!input.authorPromptMode && input.authorPromptMode === input.promptMode;
    suffix = isPin ? ` · giữ ${MODE_LABEL_VI[input.promptMode]} (prompt sửa tay)` : ' · riêng';
  }
  let stateTag: EventModeLabel['stateTag'] = null;
  let head: EventModeLabel['head'] = null;
  if (input.state === 'MISMATCH') {
    stateTag = { color: 'red', text: 'prompt lệch mode — chưa gen được' };
    head = { text: 'lệch mode', cls: 'block' };
  } else if (input.state === 'MISSING') {
    stateTag = { color: 'orange', text: 'chưa có prompt' };
    head = { text: 'chưa có prompt', cls: 'warn' };
  }
  return { modeText, suffix, stateTag, head, showModeInHead };
}

/** Danh sách event bị chặn cho Popconfirm/message.warning: ≤`limit` mã "E<order>" + "(+K)" nếu còn dư. */
export function formatBlockedOrders(orders: number[], limit = 10): string {
  const shown = orders.slice(0, limit).map((o) => `E${o}`).join(', ');
  const extra = orders.length > limit ? ` (+${orders.length - limit})` : '';
  return shown + extra;
}

/**
 * 260923 Epic2-P2 (Bước 7) — câu cảnh báo *Run remaining* khi hết việc (`authorDone` nhưng `nGenReady=0`),
 * tách 2 nhóm cách gỡ khác nhau: `mismatchMsg` (lệch mode — GIỮ câu cũ) và `missingMsg` (MISSING mà
 * `canAuthor=false` — MỚI ở Phase 2). Nhóm rỗng ⇒ `null`, không in.
 */
export interface RunRemainingBlockedMessages { mismatchMsg: string | null; missingMsg: string | null }
export function runRemainingBlockedMessages(mismatchOrders: number[], missingBlockedOrders: number[]): RunRemainingBlockedMessages {
  const mismatchMsg = mismatchOrders.length
    ? `${mismatchOrders.length} event bị chặn cửa mode (lệch mode): ${formatBlockedOrders(mismatchOrders)}. Đổi mode event về mode của prompt đang lưu, hoặc dựng lại prompt sửa tay.`
    : null;
  const missingMsg = missingBlockedOrders.length
    ? `${missingBlockedOrders.length} event chưa dựng được prompt (event đời cũ không có chuỗi nhịp / event trống / chưa có shot): ${formatBlockedOrders(missingBlockedOrders)}. Thêm nhịp hoặc chạy lại dàn shot, hoặc viết tay prompt.`
    : null;
  return { mismatchMsg, missingMsg };
}

/**
 * 260923 Epic2-P2 (N8, Bước 7) — tag ngắn ở `sb-head` cạnh tag mode (F2) khi prompt sai khuôn Tự nhiên.
 * `naturalPromptLint` rỗng/null ⇒ không tag.
 */
export function naturalLintHead(naturalPromptLint: string[] | null | undefined): { text: string; cls: 'warn' } | null {
  return naturalPromptLint && naturalPromptLint.length ? { text: 'prompt sai khuôn Tự nhiên', cls: 'warn' } : null;
}

/**
 * 260923 Epic2-P3 (M1) — mirror BE `shouldRebuildMismatch` (`filmPromptMode.ts`), CÙNG bảng ca test. Vị từ
 * "event này có nên được dựng lại khi dựng lại prompt lệch mode hàng loạt hay tự dựng lại 1 event sau khi
 * đổi mode (M2)". Chỉ đúng cho MISMATCH, KHÔNG BAO GIỜ đụng `EDITED` (kể cả canAuthor true), và `canAuthor`
 * vẫn phải đúng. Caller tự resolve `effectiveMode` bằng `resolvePromptMode(event.promptMode, planMode)`
 * TRƯỚC khi gọi — hàm này không nhận `planMode`.
 */
export function shouldRebuildMismatchOf(args: {
  effectiveMode: FilmPromptMode;
  authorPrompt: string | null | undefined;
  authorPromptMode: unknown;
  authorStatus: string | null | undefined;
  beatMeta: unknown;
  shotCount: number;
  spokenLineCount: number;
}): boolean {
  if (args.authorStatus === 'EDITED') return false;
  const state = promptModeStateOf({ effectiveMode: args.effectiveMode, authorPrompt: args.authorPrompt, authorPromptMode: args.authorPromptMode });
  if (state !== 'MISMATCH') return false;
  return canAuthorOf({ mode: args.effectiveMode, beatMeta: args.beatMeta, shotCount: args.shotCount, spokenLineCount: args.spokenLineCount });
}

/**
 * 260923 Epic2-P3 (M3) — quyết định tham số `force` cho lời gọi "Dựng lại prompt" ở nút thẻ: `EDITED` ⇒ cần
 * Popconfirm xác nhận ghi đè + `force: true`; còn lại (AUTHORED/null/…) ⇒ không cần hỏi, KHÔNG truyền `force`
 * (mặc định `false` ở route — M13, không ghi đè EDITED nếu FE lỡ tính nhầm).
 */
export interface RebuildCall { needsConfirm: boolean; force: boolean | undefined }
export function rebuildCallOf(event: { authorStatus?: string | null }): RebuildCall {
  const isEdited = event.authorStatus === 'EDITED';
  return isEdited ? { needsConfirm: true, force: true } : { needsConfirm: false, force: undefined };
}

/**
 * 260923 Epic2-P3 (Bước 3, N2 review) — N cho ô "Dựng lại ngay N prompt AI" của modal đổi mode CẢ PLAN:
 * lấy `mismatchAfter` (eventOrder) từ response `dryRun` sẵn có (Phase 1), map sang event đầy đủ trong
 * `data`, đếm event thoả `shouldRebuildMismatchOf` với `effectiveMode = resolvePromptMode(event.promptMode,
 * targetMode)` — KHÔNG gán cứng `targetMode` cho event có đè riêng (event đè riêng vẫn có thể rơi vào
 * `mismatchAfter` từ TRƯỚC, không liên quan việc đổi mode plan — xem `planModeSwitchImpact` BE, review N2).
 */
export interface RebuildableAfterModeSwitchEvent {
  eventOrder: number;
  promptMode?: unknown;
  authorPrompt?: string | null;
  authorPromptMode?: unknown;
  authorStatus?: string | null;
  beatMeta?: unknown;
  shots?: unknown[];
  dialogueLines?: Array<{ textEn?: string | null; dialogueMode?: string | null }>;
}
/**
 * 260925 (UT4, sửa sau nghiệm thu Epic 2) — nút thẻ "Dựng lại prompt" trước đây chỉ hiện khi `MISMATCH`
 * hoặc `MISSING`+`canAuthor`: một prompt `EDITED` đã lưu chữ ĐÚNG KHUÔN Kỹ thuật nhưng NHÃN mode lại là Tự
 * nhiên (`naturalPromptLint` báo "sai khuôn Tự nhiên") thì `promptModeStateOf` vẫn trả `OK` (snapshot khớp
 * nhãn) ⇒ KHÔNG có đường dựng lại — Generate vẫn xanh dù prompt sai khuôn. Gom điều kiện hiện nút vào một
 * hàm thuần để card component không tự tính rải rác 3 nơi khác nhau.
 */
export interface ShowRebuildButtonInput {
  promptModeState: PromptModeState;
  effectiveMode: FilmPromptMode;
  beatMeta: unknown;
  shotCount: number;
  spokenLineCount: number;
  naturalPromptLint?: string[] | null;
  /**
   * 260927 mục N5 — "prompt lệch bộ đồ" (`event.promptLookMismatch.mismatch`, BE tính LÚC ĐỌC — chip
   * `look:` ≠ bảng gán cảnh hiện tại). Cùng đường gỡ với MISMATCH mode: nút này, Popconfirm nếu EDITED.
   */
  lookMismatch?: boolean;
}
export function showRebuildButtonOf(args: ShowRebuildButtonInput): boolean {
  if (args.lookMismatch) return true;
  if (args.promptModeState === 'MISMATCH') return true;
  if (args.promptModeState === 'MISSING' && canAuthorOf({
    mode: args.effectiveMode, beatMeta: args.beatMeta, shotCount: args.shotCount, spokenLineCount: args.spokenLineCount,
  })) return true;
  // UT4 — prompt sai khuôn Tự nhiên (kể cả OK/EDITED): duy nhất chỗ gỡ là dựng lại (Popconfirm nếu EDITED,
  // qua `rebuildCallOf` như cũ).
  return !!(args.naturalPromptLint && args.naturalPromptLint.length);
}

export function countRebuildableAfterModeSwitch(
  mismatchAfter: number[],
  events: RebuildableAfterModeSwitchEvent[],
  targetMode: FilmPromptMode,
): number {
  const mismatchSet = new Set(mismatchAfter);
  let n = 0;
  for (const e of events) {
    if (!mismatchSet.has(e.eventOrder)) continue;
    const effectiveMode = resolvePromptMode(e.promptMode, targetMode);
    const spokenLineCount = (e.dialogueLines || []).filter((d) => d?.textEn && String(d.dialogueMode || '').toUpperCase() !== 'NONE').length;
    if (shouldRebuildMismatchOf({
      effectiveMode, authorPrompt: e.authorPrompt, authorPromptMode: e.authorPromptMode, authorStatus: e.authorStatus,
      beatMeta: e.beatMeta, shotCount: (e.shots || []).length, spokenLineCount,
    })) n++;
  }
  return n;
}
